import React, { Component, createRef } from "react";
import {
  MDBBtn,
  MDBBtnGroup,
  MDBBadge,
  MDBIcon,
  MDBSwitch,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
} from "mdb-react-ui-kit";

import AdminActorDetail from "./../AdminActorDetail/AdminActorDetail";

import "./AdminActorSection.css";

import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { mfmFramedUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { MfmPhoto } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmPhoto/MfmPhoto";

import profile_default_img from "images/profile_default.png";

const ACTOR_FORMAT_KEY = "ratio:3:4";

/* ───── Helpers de reordenamiento decimal ───── */

function computeDecimalOrder(before, after) {
  if (before == null && after == null) return 1;
  if (before == null) return after / 2;
  if (after == null) return before + 1;
  return (before + after) / 2;
}

function needsFullReindex(items) {
  if (items.length < 2) return false;
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  for (let i = 1; i < sorted.length; i++) {
    const diff = sorted[i].sortOrder - sorted[i - 1].sortOrder;
    if (diff < 1e-10) return true;
  }
  return false;
}

function fullReindex(items) {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.map((it, i) => ({ ...it, sortOrder: i + 1 }));
}

const DEFAULT_PER_PAGE = 10;

/**
 * AdminActorSection
 *
 * Vista de administración de actores. Permite:
 *  - Listar actores con su nombre y biografía resumida
 *  - Habilitar/deshabilitar (status)
 *  - Eliminar actores con confirmación (MDBModal con opción de deshabilitar)
 *  - Editar un actor o crear uno nuevo en un panel lateral
 *  - Filtrar por estado (Todos / Activos / Borrador)
 *  - Paginar la lista
 *  - Reordenar actores con drag & drop decimal inteligente
 */
export default class AdminActorSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      actors: [],
      panelOpen: false,
      selectedId: null,
      panelKey: 0,
      showDeleteModal: false,
      actorToDelete: null,
      filter: "all",
      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      dragIndex: null,
      overIndex: null,
    };
    this.cardRefs = new Map();
    this.cardsPaneRef = createRef();
  }

  componentDidMount() {
    this.loadActors();
  }

  /* ───── Carga de datos ───── */

  loadActors = () => {
    this.props.onLoading && this.props.onLoading(true);
    doFetch("TEAN", "getActors", { method: "GET" })
      .then((r) => r.json())
      .then((data) => {
        const actorsRaw = data?.DATA || [];
        const actors = actorsRaw
          .filter((a) => a)
          .sort((x, y) => {
            const sx = Number(x.sortOrder);
            const sy = Number(y.sortOrder);
            if (Number.isFinite(sx) && Number.isFinite(sy)) return sx - sy;
            return 0;
          });
        this.setState({ actors });
      })
      .catch(() => {
        toast.show({
          message: "Ha ocurrido un error al obtener los actores",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading && this.props.onLoading(false);
      });
  };

  /* ───── Imagen del actor ───── */

  getActorImage = (actor) => {
    if (actor?.imageUuid) {
      return mfmFramedUrl(actor.imageUuid, 72, 96, ACTOR_FORMAT_KEY);
    }
    return profile_default_img;
  };

  /* ───── Navegación del panel ───── */

  openEditorFor = (slug) => {
    this.setState({ selectedId: slug, panelOpen: true }, () => {
      const el = this.cardRefs.get(slug);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  openNew = () => {
    this.setState(
      (s) => ({ selectedId: null, panelOpen: true, panelKey: s.panelKey + 1 }),
      () => { this.cardsPaneRef.current?.scrollTo?.({ top: 0, behavior: "smooth" }); }
    );
  };

  closePanel = () => {
    this.setState({ panelOpen: false, selectedId: null });
  };

  onSaveActor = () => {
    this.loadActors();
  };

  /* ───── Toggle de estado ───── */

  toggleStatus = async (item) => {
    const { idActor, status, ...rest } = item;
    const newStatus = status === "published" ? "draft" : "published";

    const updatedItem = { ...item, status: newStatus };
    this.setState((s) => ({
      actors: s.actors.map((a) => (a.idActor === idActor ? updatedItem : a)),
    }));

    try {
      const payload = [{ idActor: idActor, ...rest, status: newStatus }];
      await doFetch("TEAN", "upsertActors", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then((r) => r.json().catch(() => ({})));
    } catch (e) {
      this.setState((s) => ({
        actors: s.actors.map((a) =>
          a.idActor === idActor ? { ...a, status } : a
        ),
      }));
      toast.show({
        message: "No se pudo actualizar el estado del actor",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
    }
  };

  /* ───── Drag & Drop decimal inteligente ───── */

  getSortedActors = () =>
    [...this.state.actors].sort((a, b) => a.sortOrder - b.sortOrder);

  onDragStart = (globalIndex) => (e) => {
    this.setState({ dragIndex: globalIndex, overIndex: globalIndex });
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(globalIndex));
    } catch (_) {}
  };

  onDragEnter = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDragOver = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDrop = (globalIndex) => async (e) => {
    e.preventDefault();
    const { actors } = this.state;
    let fromIdx = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (!Number.isFinite(fromIdx)) fromIdx = this.state.dragIndex;
    const toIdx = globalIndex;

    this.setState({ dragIndex: null, overIndex: null });

    if (fromIdx == null || fromIdx === toIdx) return;

    const sorted = this.getSortedActors();
    const movedItem = sorted[fromIdx];
    if (!movedItem) return;

    const rest = sorted.filter((_, i) => i !== fromIdx);
    const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;

    const before = insertAt > 0 ? rest[insertAt - 1]?.sortOrder : null;
    const after = insertAt < rest.length ? rest[insertAt]?.sortOrder : null;

    const newOrder = computeDecimalOrder(before, after);

    const updatedItem = { ...movedItem, sortOrder: newOrder };
    const updatedActors = actors.map((a) =>
      a.idActor === movedItem.idActor ? updatedItem : a
    );

    let itemsToSave;
    if (needsFullReindex(updatedActors)) {
      const reindexed = fullReindex(updatedActors);
      this.setState({ actors: reindexed });
      itemsToSave = reindexed.map((a) => ({
        idActor: a.idActor,
        sortOrder: a.sortOrder,
      }));
    } else {
      this.setState({
        actors: updatedActors.sort((a, b) => a.sortOrder - b.sortOrder),
      });
      itemsToSave = [{ idActor: movedItem.idActor, sortOrder: newOrder }];
    }

    try {
      await doFetch("TEAN", "reorderActors", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(itemsToSave),
      });
    } catch (error) {
      toast.show({
        message: "Error al guardar el nuevo orden",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
      this.loadActors();
    }
  };

  onDragEnd = () => {
    this.setState({ dragIndex: null, overIndex: null });
  };

  /* ───── Eliminación (con opción de deshabilitar) ───── */

  confirmDelete = (actor) => {
    // Abrimos en el siguiente tick para evitar cierre inmediato por el mismo click.
    window.setTimeout(() => {
      this.setState({ showDeleteModal: true, actorToDelete: actor });
    }, 0);
  };

  stopPointer = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  closeDeleteModal = () => {
    this.setState({ showDeleteModal: false, actorToDelete: null });
  };

  handleConfirmDelete = async () => {
    const actor = this.state.actorToDelete;
    if (!actor) return;
    this.props.onLoading && this.props.onLoading(true);
    try {
      const response = await doFetch(
        "TEAN",
        `deleteActor?IDActor=${actor.idActor}`,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        await this.loadActors();
        this.setState({ showDeleteModal: false, actorToDelete: null });
        toast.show({
          message: "Actor eliminado correctamente",
          type: "success",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 3000,
        });
      } else {
        toast.show({
          message:
            data?.MESSAGE ||
            "Ha ocurrido un error al borrar el actor.",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      }
    } catch (error) {
      toast.show({
        message: "Ha ocurrido un error no controlado al borrar el actor",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
    } finally {
      this.props.onLoading && this.props.onLoading(false);
    }
  };

  disableInsteadOfDelete = async () => {
    const actor = this.state.actorToDelete;
    if (!actor) return;
    this.setState({ showDeleteModal: false, actorToDelete: null });
    if (actor.status === "published") {
      await this.toggleStatus(actor);
    }
  };

  /* ───── Filtro y paginación ───── */

  getFilteredActors = () => {
    const { actors, filter } = this.state;
    const sorted = [...actors].sort((a, b) => a.sortOrder - b.sortOrder);
    if (filter === "published")
      return sorted.filter((a) => a.status === "published");
    if (filter === "draft")
      return sorted.filter((a) => a.status === "draft");
    return sorted;
  };

  /* ───── Render de tarjeta ───── */

  renderCard = (item, globalIdx) => {
      const { panelOpen, selectedId, panelKey, dragIndex, overIndex } = this.state;
    const isSelected = selectedId === item.slug && panelOpen;
    const isDragging = dragIndex === globalIdx;
    const isOver = overIndex === globalIdx;

    return (
      <div
        key={item.idActor}
        ref={(el) => this.cardRefs.set(item.slug, el)}
        className={`aa-row mb-2 ${isSelected ? "aa-row--selected" : ""} ${isDragging ? "aa-dragging" : ""} ${isOver ? "aa-over" : ""}`}
        title={item.name}
        onDragEnter={this.onDragEnter(globalIdx)}
        onDragOver={this.onDragOver(globalIdx)}
        onDrop={this.onDrop(globalIdx)}
      >
        {/* Drag handle */}
        <div
          className="aa-drag-handle"
          draggable
          onDragStart={this.onDragStart(globalIdx)}
          onDragEnd={this.onDragEnd}
        >
          <MDBIcon fas icon="grip-vertical" />
        </div>

        {/* Foto carnet */}
        <div className="aa-row-img-wrapper">
          <MfmPhoto
            uuid={item.imageUuid}
            width={72}
            height={96}
            formatKey={ACTOR_FORMAT_KEY}
            className="aa-row-img"
            fallback={profile_default_img}
            alt={item.name}
          />
        </div>

        {/* Info */}
        <div className="aa-row-info">
          <div className="aa-row-header">
            <span className="aa-title">{item.name}</span>
            <MDBBadge
              color={item.status === "published" ? "success" : "warning"}
              pill
              className="ms-2"
            >
              {item.status === "published" ? "Activo" : "Borrador"}
            </MDBBadge>
          </div>
          {item.actorRole && (
            <p className="aa-row-role">{item.actorRole}</p>
          )}
          {item.bio && (
            <p className="aa-row-bio">{item.bio}</p>
          )}
        </div>

        {/* Controles */}
        <div
          className="aa-row-controls"
          onPointerDown={this.stopPointer}
          onMouseDown={this.stopPointer}
          onClick={(e) => e.stopPropagation()}
        >
          <MDBSwitch
            id={`aa-switch-${item.idActor}`}
            checked={item.status === "published"}
            onChange={(e) => {
              e.stopPropagation();
              this.toggleStatus(item);
            }}
            label=""
            title={item.status === "published" ? "Activo" : "Inactivo"}
          />
          <MDBBtn
            size="sm"
            className="tean-btn-primary"
            type="button"
            draggable={false}
            onPointerDown={this.stopPointer}
            onMouseDown={this.stopPointer}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.openEditorFor(item.slug);
            }}
          >
            Editar
          </MDBBtn>
          <MDBBtn
            size="sm"
            color="danger"
            outline
            type="button"
            draggable={false}
            onPointerDown={this.stopPointer}
            onMouseDown={this.stopPointer}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.confirmDelete(item);
            }}
          >
            <MDBIcon fas icon="trash" />
          </MDBBtn>
        </div>
      </div>
    );
  };

  render() {
    const {
      actors,
      panelOpen,
      selectedId,
      panelKey,
      filter,
      currentPage,
      perPage,
      showDeleteModal,
      actorToDelete,
    } = this.state;

    const sorted = [...actors].sort((a, b) => a.sortOrder - b.sortOrder);
    const filtered = this.getFilteredActors();
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageItems = filtered.slice(
      (safeCurrentPage - 1) * perPage,
      safeCurrentPage * perPage
    );

    const totalCount = actors.length;
    const publishedCount = actors.filter(
      (a) => a.status === "published"
    ).length;
    const draftCount = totalCount - publishedCount;

    return (
      <section
        className={`admin-actors-layout ${panelOpen ? "is-open" : ""}`}
      >
        {/* Pane izquierdo: listado */}
        <div className="cards-pane" ref={this.cardsPaneRef}>
          <div className="cards-toolbar">
            <MDBBtnGroup size="sm">
              <MDBBtn
                color={filter === "all" ? "primary" : "light"}
                onClick={() =>
                  this.setState({ filter: "all", currentPage: 1 })
                }
              >
                Todos
                <MDBBadge className="ms-1" color="dark" pill>
                  {totalCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "published" ? "success" : "light"}
                onClick={() =>
                  this.setState({ filter: "published", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye" className="me-1" />
                Activos
                <MDBBadge className="ms-1" color="dark" pill>
                  {publishedCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "draft" ? "warning" : "light"}
                onClick={() =>
                  this.setState({ filter: "draft", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye-slash" className="me-1" />
                Borrador
                <MDBBadge className="ms-1" color="dark" pill>
                  {draftCount}
                </MDBBadge>
              </MDBBtn>
            </MDBBtnGroup>

            <MDBBtn
              color="success"
              size="sm"
              className="ms-auto"
              onClick={this.openNew}
            >
              Nuevo actor
            </MDBBtn>
          </div>

          {/* Lista de tarjetas con drag & drop */}
          <div className="aa-list">
            {pageItems.map((item) => {
              const globalIdx = sorted.findIndex(
                (a) => a.idActor === item.idActor
              );
              return this.renderCard(item, globalIdx);
            })}
          </div>

          {/* Paginador */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination pagination-sm">
                <li
                  className={`page-item ${safeCurrentPage <= 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      this.setState({ currentPage: safeCurrentPage - 1 })
                    }
                  >
                    &laquo;
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i}
                    className={`page-item ${i + 1 === safeCurrentPage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => this.setState({ currentPage: i + 1 })}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safeCurrentPage >= totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      this.setState({ currentPage: safeCurrentPage + 1 })
                    }
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>

        {/* Pane derecho: detalle */}
        <div className="detail-pane">
          {panelOpen && (
            <div className="detail-card">
              <AdminActorDetail
                key={selectedId ?? ('new-' + panelKey)}
                actorId={selectedId}
                onClose={this.closePanel}
                onSave={this.onSaveActor}
                onHandleNavLinks={this.props.onHandleNavLinks}
              />
            </div>
          )}
        </div>

        {/* Modal de eliminación (con opción de deshabilitar) */}
        <MDBModal
          show={showDeleteModal}
          onHide={this.closeDeleteModal}
          tabIndex="-1"
        >
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon
                    fas
                    icon="exclamation-triangle"
                    className="text-warning me-2"
                  />
                  Confirmar eliminación
                </MDBModalTitle>
                <MDBBtn
                  className="btn-close"
                  color="none"
                  onClick={this.closeDeleteModal}
                />
              </MDBModalHeader>
              <MDBModalBody>
                <p>
                  ¿Está seguro de que desea eliminar el actor{" "}
                  <strong>"{actorToDelete?.name}"</strong>?
                </p>
                <p className="text-muted small">
                  Considera <strong>deshabilitar</strong> el actor en lugar de eliminarlo.
                  Los actores deshabilitados no se muestran al público pero se pueden recuperar.
                </p>
              </MDBModalBody>
              <MDBModalFooter className="d-flex justify-content-between">
                <MDBBtn
                  color="secondary"
                  outline
                  onClick={this.closeDeleteModal}
                >
                  Cancelar
                </MDBBtn>
                <div>
                  <MDBBtn
                    color="warning"
                    className="me-2"
                    onClick={() => this.disableInsteadOfDelete()}
                  >
                    <MDBIcon fas icon="eye-slash" className="me-1" />
                    Deshabilitar
                  </MDBBtn>
                  <MDBBtn
                    color="danger"
                    onClick={() => this.handleConfirmDelete()}
                  >
                    <MDBIcon fas icon="trash" className="me-1" />
                    Eliminar
                  </MDBBtn>
                </div>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </section>
    );
  }
}